import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { DateFormat, Locale } from "@porsche-customer/slfinrtl-context-types"
import { isLanguage } from "@porsche-customer/slfinrtl-context-types"
import type { InputTextInputEventDetail } from "@porsche-design-system/components-react"
import { PIcon, PInputText } from "@porsche-design-system/components-react"
import { isIsoDate } from "@slfinrtl/ui-bff-routes"
import flatpickrLocalesModule from "flatpickr/dist/l10n"
import type { key as FlatpickrKey } from "flatpickr/dist/types/locale"
import type { Ref } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import type DatePicker from "react-flatpickr"
import FlatpickrModule from "react-flatpickr"
import { Tooltip } from "../../tooltip/Tooltip"
import { FlatpickrWrapper } from "./FlatpickrWrapper"
import { isValidDateValue, parseIsoDate, toOnChangeValue } from "./datepicker-utils"
import { createTopLayerCalendar } from "./topLayerCalendar"

// workarounds for outdated package needed since vite 8
const Flatpickr = (FlatpickrModule as any).default ?? FlatpickrModule

// unlike above, we cannot check for ".default" here, because the flatpickr locales object contains a locale called "default" 😅
const isFlatpickrLocalesObject =
	typeof flatpickrLocalesModule === "object" && flatpickrLocalesModule !== null && "ar" in flatpickrLocalesModule
const flatpickrLocales = isFlatpickrLocalesObject
	? flatpickrLocalesModule
	: (flatpickrLocalesModule as unknown as { default: typeof flatpickrLocalesModule }).default

export interface DatepickerProps {
	/**
	 * Format of the displayed date. Defaults to the one of the dealer context, which is required if it is not given.
	 */
	dateFormat?: DateFormat

	/**
	 * Controls if the datepicker is disabled.
	 */
	disabledHint?: string

	/**
	 * Show or hide label and description text.
	 * For better accessibility it is recommended to show the label.
	 */
	hideLabel?: boolean

	/**
	 * Will be displayed as a floating label in the Input field
	 */
	label?: string

	/**
	 * Shows an info icon next to the label, which opens a tooltip with this text
	 */
	labelTooltip?: string

	/**
	 * Locale of the calendar. Defaults to the one of the dealer context, which is required if it is not given.
	 */
	locale?: Locale

	name: string

	/**
	 * Will be displayed underneath the label in a smaller font
	 */
	description?: string

	/**
	 * The most recent ISO-date to select
	 */
	maxDate?: string

	/** The message styled depending on validation state. */
	message?: string

	/**
	 * The oldest ISO-date to select
	 */
	minDate?: string

	/**
	 * Callback that is called when the entered or selected date is valid
	 */
	onChange?: (value: { input: string; isoDate: string | null; isValid: boolean }) => void

	ref?: Ref<HTMLInputElement>

	/**
	 * Whether this is a required input or not. Will be indicated by a star next to the label. Default false
	 */
	required?: boolean

	/**
	 * The validation state.
	 * @default "none"
	 */
	state?: "none" | "error" | "success"

	/**
	 * An iso date string with which the datepicker can be initialized
	 */
	value?: string | null
}

/**
 * A Datepicker allows the user to select a date from a calendar.
 * This thin datepicker wrapper uses https://github.com/flatpickr/flatpickr under the hood.
 */
export const Datepicker = ({ dateFormat, locale, ...props }: DatepickerProps) =>
	dateFormat && locale ? (
		<DatepickerWithSettings {...props} dateFormat={dateFormat} locale={locale} />
	) : (
		<DatepickerWithDealerContext {...props} dateFormat={dateFormat} locale={locale} />
	)

// the dealer context is only required if the date settings are not given
const DatepickerWithDealerContext = ({ dateFormat, locale, ...props }: DatepickerProps) => {
	const dealerContext = useDealerContext()

	return (
		<DatepickerWithSettings
			{...props}
			dateFormat={dateFormat ?? dealerContext.dateFormat}
			locale={locale ?? dealerContext.locale}
		/>
	)
}

const DatepickerWithSettings = (props: DatepickerProps & Required<Pick<DatepickerProps, "dateFormat" | "locale">>) => {
	const {
		dateFormat,
		description,
		disabledHint,
		hideLabel,
		label,
		labelTooltip,
		locale,
		maxDate,
		message,
		minDate,
		name,
		onChange,
		ref,
		required,
		state,
		value,
		...rest
	} = props

	const language = useMemo(() => {
		const contextLanguage = locale.split("-")[0]
		return isLanguage(contextLanguage) ? contextLanguage : "en"
	}, [locale])

	const flatPickrRef = useRef<DatePicker>(null)
	const topLayerCalendar = useMemo(() => createTopLayerCalendar(), [])
	// flatpickr does not fire onClose when it is destroyed while open, e.g. together with a closing modal
	useEffect(() => topLayerCalendar.stop, [topLayerCalendar])

	const lang = useMemo(
		() =>
			flatpickrLocales[language as FlatpickrKey] ??
			flatpickrLocales[navigator.language.split("-")[0] as FlatpickrKey] ??
			flatpickrLocales.default,
		[language],
	)

	const isValueValid = useMemo(() => isValidDateValue(value), [value])

	const [inputValue, setInputValue] = useState(() => value && parseIsoDate(value, dateFormat))

	useEffect(() => {
		if (isValueValid || value === "") {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setInputValue(value && parseIsoDate(value, dateFormat))
		}
	}, [value, dateFormat, isValueValid, setInputValue])

	const onFlatpickrChange = (_dates: Date[], currentDateString: string) => {
		if (
			!onChange ||
			!currentDateString ||
			(typeof currentDateString === "string" && !isIsoDate(currentDateString))
		) {
			return
		}
		onChange({
			input: currentDateString,
			isoDate: currentDateString,
			isValid: true,
		})
	}

	const onInputChange = (event: CustomEvent<InputTextInputEventDetail>) => {
		if (!onChange) {
			return
		}

		const val = (event.target as HTMLInputElement).value
		setInputValue(val)
		onChange(toOnChangeValue(val, dateFormat))
	}

	const onWrapperClick = () => {
		if (disabledHint) {
			return
		}

		flatPickrRef.current?.flatpickr?.open()
	}

	return (
		<FlatpickrWrapper {...rest} onClick={onWrapperClick}>
			<Flatpickr
				ref={flatPickrRef}
				value={isValueValid ? (value ?? undefined) : undefined}
				disabled={!!disabledHint}
				onChange={onFlatpickrChange}
				onReady={topLayerCalendar.onReady}
				onOpen={topLayerCalendar.onOpen}
				onClose={topLayerCalendar.onClose}
				options={{
					allowInput: true,
					clickOpens: false,
					locale: lang,
					maxDate,
					minDate,
					position: topLayerCalendar.position,
					static: true,
					wrap: true,
				}}
			>
				{/* dummy element to make flatpickr happy, since data-input does not work on pds web component */}
				<input type="text" hidden data-input />

				<Tooltip content={disabledHint}>
					<PInputText
						ref={ref}
						name={name}
						description={description}
						label={labelTooltip ? undefined : label}
						hideLabel={hideLabel}
						state={state}
						message={message}
						value={inputValue ?? ""}
						autoComplete="off"
						required={required}
						disabled={!!disabledHint}
						onInput={onInputChange}
					>
						{labelTooltip && (
							<span slot="label">
								{label}
								<Tooltip content={labelTooltip}>
									<PIcon name="information" />
								</Tooltip>
							</span>
						)}
						<PIcon slot="end" name="calendar" color="contrast-medium" aria-hidden />
					</PInputText>
				</Tooltip>
			</Flatpickr>
		</FlatpickrWrapper>
	)
}
